import { Grid, Box, Pagination, Typography, CircularProgress, Alert, Paper } from '@mui/material'
import type { ChangeEvent } from 'react'
import type { TPublisherRequest } from '@modules/types'
import type { TFormattedRequestData } from '@utils/requestFormatter'
import { RequestShow } from './show'

interface RequestsGridProps {
  requests: TPublisherRequest[]
  formattedRequestsData: Record<number, TFormattedRequestData>
  isLoading: boolean
  isError: boolean
  totalPages: number
  currentPage: number
  onPageChange: (event: ChangeEvent<unknown>, page: number) => void
  expandedCards: Set<number>
  onToggleCard: (requestId: number) => void
}

export const RequestsGrid = ({
  requests,
  formattedRequestsData,
  isLoading,
  isError,
  totalPages,
  currentPage,
  onPageChange,
  expandedCards,
  onToggleCard,
}: RequestsGridProps) => {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Error loading requests. Please try again later.
      </Alert>
    )
  }

  if (requests.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          No requests found
        </Typography>
      </Box>
    )
  }

  return (
    <Paper>
      <Grid container spacing={3} sx={{ transition: 'all 0.3s ease-in-out' }}>
        {requests.map(request => (
          <Grid size={12} key={request.id} sx={{ transition: 'all 0.3s ease-in-out' }}>
            <RequestShow
              request={request}
              formattedData={formattedRequestsData[request.id]}
              isExpanded={expandedCards.has(request.id)}
              onToggleExpand={() => onToggleCard(request.id)}
              sharingRequestId={request.id}
            />
          </Grid>
        ))}
      </Grid>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={onPageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Paper>
  )
}
